import { Editor } from '@tiptap/vue-2';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { BDropdown, BDropdownItemButton } from 'bootstrap-vue';

function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = !0,
      o = !1;
    try {
      if (i = (t = t.call(r)).next, 0 === l) ; else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = !0, n = r;
    } finally {
      try {
        if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r || "default");
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}
function _defineProperty(obj, key, value) {
  key = _toPropertyKey(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}
function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];
  return arr2;
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

/**
 * Makes an 'enum' (just a frozen object), with the same behavior as a Typescript enum.
 *
 * Accepts an array containing keys and/or key-value pairs, where values are `string`s or `number`s:
 *
 * ```js
 * const MY_ENUM = makeEnum(['A', 'Be Cool!', ['Cee', 10], ['D', 'ok']]);
 *
 * MY_ENUM.A; // Symbol(A)
 * const a = MY_ENUM.A; // Symbol(A)
 * MY_ENUM[a]; // 'A'
 *
 * // this doesn't work as symbols are unique:
 * MY_ENUM[Symbol('A')];
 * ```
 *
 * The enum is just an object:
 *
 * ```js
 * {
 *   A: Symbol(A),
 *   BE_COOL: Symbol(Be Cool!),
 *   CEE: Symbol(10),
 *   D: Symbol(ok),
 *   [Symbol(A)]: 'A',
 *   [Symbol(Be Cool!)]: 'BE_COOL',
 *   [Symbol(10)]: 'CEE',
 *   [Symbol(ok)]: 'D'
 * }
 * ```
 *
 * Whitespace is replaces with underscores, and non-alphanumeric characters are removed.
 *
 * @param { ([string, string|number|undefined] | string)[] } entries
 *
 * @returns { Readonly<{[key: string|Symbol]: string|Symbol }> }
 */
var makeEnum = function makeEnum(entries) {
  // guarantee array of [key, val] pairs, keys formatted, vals as Symbols
  var parsed = entries.map(function (entry_or_key) {
    var key_only = typeof entry_or_key === 'string';
    var key = key_only ? entry_or_key : entry_or_key[0];
    var val = key_only ? Symbol(key) : Symbol(entry_or_key[1]);
    return [key.replace(/\s/g, '_').replace(/\W/g, '').toUpperCase(), val];
  });
  var out_enum = parsed.reduce(function (out_enum, _ref) {
    var _ref2 = _slicedToArray(_ref, 2),
      key = _ref2[0],
      val = _ref2[1];
    out_enum = _objectSpread2(_objectSpread2({}, out_enum), {}, _defineProperty(_defineProperty({}, key, val), val, key));
    return out_enum;
  }, {});
  return Object.freeze(out_enum);
};

const HEADING_LEVELS = makeEnum(Array.from({
  length: 6,
}, (_, i) => `Heading ${i + 1}`));

const getNextId = (function () {
  let id = 0;
  return () => id++;
})();

var TipTap = {
render: function(){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"tiptap-container border border-gray-900 rounded",attrs:{"id":_vm.id}},[_c('div',{staticClass:"tiptap-menu px-4 py-2 d-flex border-bottom border-gray-900"},[_c('b-dropdown',{directives:[{name:"tooltip",rawName:"v-tooltip",value:('Toggle heading level'),expression:"'Toggle heading level'"}],scopedSlots:_vm._u([{key:"button-content",fn:function(){return [_c('i',{staticClass:"fa fa-heading"})]},proxy:true}])},[_vm._v(" "),_vm._l((_vm.heading_level_callbacks),function(ref){
var label = ref.label;
var is_active = ref.is_active;
var cb = ref.cb;
return _c('b-dropdown-item-button',{key:label,attrs:{"active":is_active},on:{"click":cb}},[_vm._v("\n        "+_vm._s(label)+"\n      ")])})],2),_vm._v(" "),_vm._l((_vm.toggleable_controls),function(ref,i){
var icon = ref.icon;
var hover_text = ref.hover_text;
var cb = ref.cb;
var button_classes = ref.button_classes;
return _c('button',{directives:[{name:"tooltip",rawName:"v-tooltip",value:(hover_text),expression:"hover_text"}],key:i,staticClass:"btn btn-icon",class:button_classes,on:{"click":cb}},[_c('i',{class:icon})])})],2),_vm._v(" "),_c('div',{ref:"content_target"})])},
staticRenderFns: [],
  components: {
    BDropdown,
    BDropdownItemButton,
  },
  model: {
    prop: 'value',
    'event': 'input',
  },
  props: {
    value: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: `tiptap-${getNextId()}`,
    },
    inputId: {
      type: String,
      default: `tiptap-input-${getNextId()}`,
    },
    placeholder: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    editor: null,
  }),
  computed: {
    /**
     * Array of `{ label: string, cb: function }`
     */
    heading_level_callbacks() {
      if (!this.editor) return;

      const with_callbacks = [];
      for (let i = Object.keys(HEADING_LEVELS).length; i > 0; --i) {
        const key = `HEADING_${i}`;
        const val = HEADING_LEVELS[key];

        with_callbacks.push({
          label: val.description,
          is_active: this.editor.isActive('heading', {
            level: i,
          }),
          cb: () => this.editor.chain().focus().toggleHeading({
            level: i,
          }).run(),
        });
      }

      return with_callbacks.reverse();
    },
    toggleable_controls() {
      return [
        {
          icon: 'fa fa-bold',
          hover_text: 'Toggle bold',
          cb: () => this.editor.chain().focus().toggleBold().run(),
        },
        {
          icon: 'fa fa-italic',
          hover_text: 'Toggle italics',
          cb: () => this.editor.chain().focus().toggleItalic().run(),
        },
        {
          icon: 'fa fa-underline',
          hover_text: 'Toggle underline',
          cb: () => this.editor.chain().focus().toggleUnderline().run(),
        },
        {
          icon: 'fa fa-strikethrough',
          hover_text: 'Toggle strikethrough',
          cb: () => this.editor.chain().focus().toggleStrike().run(),
        },
        {
          icon: 'fa fa-list-ol',
          hover_text: 'Toggle numbered (ordered) list',
          cb: () => this.editor.chain().focus().toggleOrderedList().run(),
        },
        {
          icon: 'fa fa-list',
          hover_text: 'Toggle bullet list',
          cb: () => this.editor.chain().focus().toggleBulletList().run(),
        },
        {
          icon: 'fa fa-link',
          hover_text: 'Set or clear text hyperlink',
          cb: this.setLink,
        },
        {
          icon: 'fa fa-times',
          hover_text: 'Clear all formatting from selection',
          button_classes: 'ml-2 pl-2 border-left border-gray-900',
          cb: () => this.editor.chain().focus().unsetAllMarks().run(),
        },
      ];
    },
  },
  watch: {
    // updates the underlying editor on value change (from v-model in user code)
    value(value) {
      if (this.editor.getHTML() === value) { // dont update if somehow eq.
        return;
      }

      const { from, to } = this.editor.state.selection;

      this.editor.commands.setContent(
        value.replaceAll(/<p([^>]*)><br\/?>/gi, '<p$1>')
          .replaceAll(/<li([^>]*)><br\/?>/gi, "<li$1><p></p>"),
        false,
        {
          preserveWhitespace: 'full',
        },
      );

      this.editor.commands.setTextSelection({ from, to });
    },
  },
  mounted() {
    this.editor = new Editor({
      element: this.$refs['content_target'],
      content: this.value,
      parseOptions: {
        preserveWhitespace: 'full',
      },
      extensions: [
        StarterKit.configure({
          heading: HEADING_LEVELS,
        }),
        Underline,
        Link,
        Placeholder.configure({
          placeholder: this.placeholder,
        }),
      ],
      onUpdate: () => {
        // we need to do a bit of parsing since the lib gives a couple of things in a different format
        this.$emit(
          'input',
          this.editor.getHTML()
            .replaceAll(/<p([^>]*)><\/p>/gi, '<p$1><br></p>') // empty p needs breakline content
            .replaceAll(/<li([^>]*)><p(?:[^>]*)>([^>]*?)<\/p><\/li>/gi, "<li$1>$2</li>"), // remove wrapper p tags for li content
        );
      },
    });

    this.$refs['content_target'].querySelector('.tiptap').id = this.inputId;
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    setLink() {
      const previousUrl = this.editor.getAttributes('link').href;
      const url = window.prompt('Enter text URL (or leave empty to clear a previous link)', previousUrl);

      // cancelled
      if (url === null) {
        return;
      }

      // empty
      if (url === '') {
        this.editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .unsetLink()
          .run();

        return;
      }

      // update link
      this.editor
        .chain()
        .focus()
        .extendMarkRange('link')
        .setLink({ href: url })
        .run();
    },
  },
};

function install(Vue) {
  if (install.installed) return;
  install.installed = true;
  Vue.component("tip-tap", TipTap);
}
var plugin = {
  install: install
};
var GlobalVue = null;
if (typeof window !== "undefined") {
  GlobalVue = window.Vue;
} else if (typeof global !== "undefined") {
  GlobalVue = global.vue;
}
if (GlobalVue) {
  GlobalVue.use(plugin);
}
TipTap.install = install;

export { TipTap as default };
